import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminHeader from '../components/AdminHeader';
import ConfirmModal from '../components/ConfirmModal';
import { io } from 'socket.io-client';
import { escapeCSV, downloadCSV, formatDateTime } from '../utils/csvExport';
import './AdminOrdersPage.css';

function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [shippingFilter, setShippingFilter] = useState('All');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('All');
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelOrderData, setCancelOrderData] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const ordersPerPage = 12;

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API}/api/orders/all`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (!res.ok) throw new Error('Failed to fetch orders');
        const data = await res.json();
        setOrders(data);
      } catch (err) {
        console.error(err);
        alert('Could not load orders.');
      }
    };
    fetchOrders();
    

    // realtime updates: refetch orders when admin summary updates
    const token = localStorage.getItem('token');
    let socket;
    try {
      socket = io(process.env.REACT_APP_API, { auth: { token } });
      socket.on('admin:summary', () => {
        // refetch orders to reflect latest statuses/changes
        try { fetchOrders(); } catch (e) { }
      });
    } catch (e) {
      // ignore socket errors; page will still poll/refetch via actions
    }

    return () => { try { if (socket) socket.disconnect(); } catch (e) {} };
  }, []);

  // Filter and search logic
  const filteredOrders = orders.filter(order => {
    const statusMatch = statusFilter === 'All' || (order.status || 'Pending') === statusFilter;
    const shippingMatch = shippingFilter === 'All' || 
      (shippingFilter === 'Store Pickup' && order.shippingMethod === 'pickup') ||
      (shippingFilter === 'Delivery' && order.shippingMethod !== 'pickup');
    const paymentMatch = paymentMethodFilter === 'All' ||
      (paymentMethodFilter === 'Online' && order.paymentMethod !== 'cash') ||
      (paymentMethodFilter === 'Cash on Delivery' && (order.paymentMethod === 'cash' || order.paymentMethod === 'cod'));
    const searchMatch = searchQuery === '' || 
      order._id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order._id.slice(-8).toLowerCase().includes(searchQuery.toLowerCase());
    return statusMatch && shippingMatch && paymentMatch && searchMatch;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const startIndex = (currentPage - 1) * ordersPerPage;
  const endIndex = startIndex + ordersPerPage;
  const currentOrders = filteredOrders.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  const handleFilterChange = (filter) => {
    setStatusFilter(filter);
    setCurrentPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const confirmCancelOrder = async () => {
    if (!cancelOrderData) return;
    try {
      let adminMsg = '';
      try { 
        adminMsg = window.prompt('Optional: Add a message to the customer explaining why you cancelled this order:', '') || ''; 
      } catch (e) { 
        adminMsg = ''; 
      }
      const res = await fetch(`${process.env.REACT_APP_API}/api/orders/${cancelOrderData._id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ status: 'Cancelled', message: adminMsg }),
      });
      if (!res.ok) {
        let msg = 'Failed to update status';
        try { const data = await res.json(); if (data && data.message) msg = data.message; } catch {}
        throw new Error(msg);
      }
      const updated = await res.json();
      const refetchRes = await fetch(`${process.env.REACT_APP_API}/api/orders/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const allOrders = await refetchRes.json();
      const newOrder = allOrders.find(o => o._id === updated._id);
      setSelectedOrder(newOrder || updated);
      setOrders(allOrders);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to cancel order');
      setShowErrorModal(true);
    }
    setCancelOrderData(null);
  };

  const exportToCSV = () => {
    let csvContent = 'Order ID,Date,Customer Name,Customer Email,Items,Total Amount,Status,Payment Method,Payment Status,Delivery Province,Delivery City\n';
    
    filteredOrders.forEach(order => {
      const orderId = escapeCSV(String(order._id || '').slice(-8));
      const date = escapeCSV(formatDateTime(order.createdAt));
      const customerName = escapeCSV(
        (order.deliveryDetails?.firstName || order.deliveryDetails?.lastName)
          ? `${order.deliveryDetails.firstName || ''} ${order.deliveryDetails.lastName || ''}`.trim()
          : (order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : 'N/A')
      );
      const customerEmail = escapeCSV(order.deliveryDetails?.email || order.user?.email || 'N/A');
      const itemCount = escapeCSV(order.items?.length || 0);
      const total = escapeCSV(order.totalAmount || 0);
      const status = escapeCSV(order.status || 'Pending');
      const paymentMethod = escapeCSV(order.paymentMethod || 'cash');
      const paymentStatus = escapeCSV(order.paymentStatus || 'pending');
      const province = escapeCSV(order.deliveryDetails?.province || '');
      const city = escapeCSV(order.deliveryDetails?.city || '');
      
      csvContent += `${orderId},${date},${customerName},${customerEmail},${itemCount},${total},${status},${paymentMethod},${paymentStatus},${province},${city}\n`;
    });
    
    const filename = `orders_backup_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  return (
    <div className="admin-orders-page">
      <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }} />
      <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
          <AdminHeader />
        </div>
      </div>
      
      <div className="admin-orders-content">
        {/* Header Section */}
        <div className="page-header">
          <div className="page-title-row">
            <h1 className="page-title">All Orders</h1>
            <div className="stats-badges">
              <div className="stat-badge">
                {statusFilter === 'All' ? 'All Orders' : statusFilter + ' Orders'}: {filteredOrders.length}
              </div>
              <motion.button
                onClick={exportToCSV}
                style={{
                  background: '#2e7d32',
                  color: '#fff',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(46,125,50,0.2)'
                }}
                whileHover={{ scale: 1.02, backgroundColor: '#1b5e20' }}
                whileTap={{ scale: 0.98 }}
              >
                📊 Export CSV
              </motion.button>
            </div>
          </div>
        </div>

        {/* Search Section */}
        <motion.div 
          className="search-section"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="search-bar-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search by Order # (full ID or last 8 characters)..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              aria-label="Search orders"
            />
          </div>
        </motion.div>

        {/* Filters Section */}
        <motion.div 
          className="filters-section"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <div className="filters-row">
            <div className="filter-group">
              <label htmlFor="statusFilter" className="filters-label">Status</label>
              <select id="statusFilter" value={statusFilter} onChange={e => handleFilterChange(e.target.value)} className="filter-select">
                {['All','Pending','Awaiting Payment','Payment Confirmed','Preparing','Ready for Pickup','Shipped','Completed','Cancelled'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="shippingFilter" className="filters-label">Shipping</label>
              <select id="shippingFilter" value={shippingFilter} onChange={e => setShippingFilter(e.target.value)} className="filter-select">
                {['All', 'Store Pickup', 'Delivery'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="paymentMethodFilter" className="filters-label">Payment</label>
              <select id="paymentMethodFilter" value={paymentMethodFilter} onChange={e => setPaymentMethodFilter(e.target.value)} className="filter-select">
                {['All', 'Online', 'Cash on Delivery'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {/* Orders Grid */}
        {orders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📦</div>
            <div className="empty-state-text">No orders found</div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <motion.div 
            className="no-results"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="empty-state-icon">🔍</div>
            <div className="empty-state-text">No orders match your search criteria</div>
          </motion.div>
        ) : (
          <>
            <div className="orders-grid">
              {currentOrders.map((order, idx) => {
                const item = order.items[0];
                return (
                  <motion.div 
                    key={order._id} 
                    className="order-card"
                    onClick={() => setSelectedOrder(order)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.3, 
                      delay: idx * 0.05,
                      ease: [0.4, 0, 0.2, 1]
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    layout
                  >
                    <div className="order-card-header">
                      <h3 className="order-number">#{order._id.slice(-8)}</h3>
                      <span className={`status-badge ${(order.status || 'Pending').toLowerCase()}`}>
                        {order.status || 'Pending'}
                      </span>
                    </div>
                    
                    <div className="order-image-container">
                      {item && item.product && typeof item.product === 'object' && item.product.image ? (
                        <img src={item.product.image} alt={item.product.name || 'Product'} />
                      ) : (
                        <div style={{ color: '#cbd5e0', fontSize: '3rem' }}>📦</div>
                      )}
                    </div>
                    
                    <div className="order-details">
                      <div className="detail-row">
                        <span className="detail-icon">👤</span>
                        <span className="detail-value">
                          {order.deliveryDetails.firstName} {order.deliveryDetails.lastName}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-icon">📧</span>
                        <span className="detail-value">{order.deliveryDetails.email}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-icon">{order.shippingMethod === 'pickup' ? '🏪' : '🚚'}</span>
                        <span className="detail-value" style={{ 
                          fontWeight: order.shippingMethod === 'pickup' ? 600 : 400,
                          color: order.shippingMethod === 'pickup' ? '#7ba93a' : '#64748b'
                        }}>
                          {order.shippingMethod === 'pickup' ? 'Store Pickup' : 
                           order.shippingMethod === 'lalamove' ? 'Lalamove' : 
                           'Standard Delivery'}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-icon">📅</span>
                        <span className="detail-value">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-icon">💰</span>
                        <span className="detail-label">Total:</span>
                        <span className="detail-value" style={{ fontWeight: 700, color: '#7a9c3a' }}>
                          ₱{order.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div 
                className="pagination-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <div className="pagination-info">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredOrders.length)} of {filteredOrders.length} orders
                </div>
                <div className="pagination-controls">
                  <motion.button 
                    className="page-btn"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Go to first page"
                  >
                    First
                  </motion.button>
                  <motion.button 
                    className="page-btn"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Go to previous page"
                  >
                    Previous
                  </motion.button>
                  
                  {/* Page numbers */}
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNum = index + 1;
                    if (
                      pageNum === 1 || 
                      pageNum === totalPages || 
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <motion.button
                          key={pageNum}
                          className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                          whileTap={{ scale: 0.95 }}
                          aria-label={`Go to page ${pageNum}`}
                          aria-current={currentPage === pageNum ? 'page' : undefined}
                        >
                          {pageNum}
                        </motion.button>
                      );
                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                      return <span key={pageNum} className="page-ellipsis">...</span>;
                    }
                    return null;
                  })}
                  
                  <motion.button 
                    className="page-btn"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Go to next page"
                  >
                    Next
                  </motion.button>
                  <motion.button 
                    className="page-btn"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    whileTap={{ scale: 0.95 }}
                    aria-label="Go to last page"
                  >
                    Last
                  </motion.button>
                </div>
              </motion.div>
            )}
          </>
        )}
        
        {/* Modal for order details */}
        <AnimatePresence>
          {selectedOrder && (
            <motion.div 
              className="modal-overlay"
              onClick={() => setSelectedOrder(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div 
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              >
                {/* Modal Header */}
                <div className="modal-header">
                  <h2 className="modal-title">Order #{selectedOrder._id.slice(-8)}</h2>
                  <button 
                    className="modal-close-btn"
                    onClick={() => setSelectedOrder(null)}
                    aria-label="Close modal"
                  >
                    ×
                  </button>
                </div>

                {/* Modal Body */}
                <div className="modal-body">
                  <div className="modal-grid">
                    {/* Left Column: Order Information */}
                    <div className="modal-section">
                      <h3 className="section-title">Order Information</h3>
                      
                      <div className="info-group">
                        <div className="info-label">Order ID</div>
                        <div className="info-value">{String(selectedOrder._id).slice(-8)}</div>
                      </div>

                      <div className="info-group">
                        <div className="info-label">Status</div>
                        <span className={`status-badge ${(selectedOrder.status || 'Pending').toLowerCase()}`}>
                          {selectedOrder.status || 'Pending'}
                        </span>
                      </div>

                      <div className="info-group">
                        <div className="info-label">Order Date</div>
                        <div className="info-value">{new Date(selectedOrder.createdAt).toLocaleString()}</div>
                      </div>

                      <div className="info-group">
                        <div className="info-label">Customer Name</div>
                        <div className="info-value">
                          {selectedOrder.deliveryDetails.firstName} {selectedOrder.deliveryDetails.lastName}
                        </div>
                      </div>

                      <div className="info-group">
                        <div className="info-label">Email</div>
                        <div className="info-value">{selectedOrder.deliveryDetails.email}</div>
                      </div>

                      <div className="info-group">
                        <div className="info-label">Phone Number</div>
                        <div className="info-value">{selectedOrder.deliveryDetails.phoneNumber}</div>
                      </div>

                      <div className="info-group">
                        <div className="info-label">Shipping Method</div>
                        <div className="info-value" style={{
                          fontWeight: 600,
                          color: selectedOrder.shippingMethod === 'pickup' ? '#7ba93a' : '#475569',
                          background: selectedOrder.shippingMethod === 'pickup' ? '#f0f7ed' : 'transparent',
                          padding: selectedOrder.shippingMethod === 'pickup' ? '6px 12px' : '0',
                          borderRadius: selectedOrder.shippingMethod === 'pickup' ? '8px' : '0',
                          display: 'inline-block'
                        }}>
                          {selectedOrder.shippingMethod === 'pickup' && '🏪 '}
                          {selectedOrder.shippingMethod === 'pickup' ? 'Store Pickup' : 
                           selectedOrder.shippingMethod === 'lalamove' ? '🚚 Lalamove Delivery' : 
                           '🚚 Standard Delivery'}
                        </div>
                      </div>

                      {selectedOrder.shippingMethod !== 'pickup' && (
                      <div className="info-group">
                        <div className="info-label">Shipping Address</div>
                        <div className="info-value">
                          {selectedOrder.deliveryDetails.fullAddress}<br />
                          {selectedOrder.deliveryDetails.city}, {selectedOrder.deliveryDetails.region} {selectedOrder.deliveryDetails.postalCode}<br />
                          Philippines
                        </div>
                      </div>
                      )}

                      {selectedOrder.deliveryDetails.landmark && selectedOrder.shippingMethod !== 'pickup' && (
                        <div className="info-group">
                          <div className="info-label">📍 Landmark</div>
                          <div className="info-value" style={{ fontStyle: 'italic' }}>
                            {selectedOrder.deliveryDetails.landmark}
                          </div>
                        </div>
                      )}

                      {selectedOrder.deliveryDetails.deliveryNotes && selectedOrder.shippingMethod !== 'pickup' && (
                        <div className="info-group">
                          <div className="info-label">🚚 Delivery Notes</div>
                          <div className="info-value" style={{ 
                            background: '#fffbea', 
                            border: '1.5px solid #ffd54f', 
                            borderRadius: 8,
                            padding: 12 
                          }}>
                            {selectedOrder.deliveryDetails.deliveryNotes}
                          </div>
                        </div>
                      )}

                      {/* Messages */}
                      {selectedOrder.messages && selectedOrder.messages.length > 0 && (
                        <div className="info-group">
                          <div className="info-label">Messages</div>
                          <div className="info-value">
                            {selectedOrder.messages.map((m, idx) => (
                              <div key={idx} style={{ marginBottom: 8, padding: 8, borderRadius: 8, background: '#fff', border: '1px solid #eef2f6' }}>
                                <div style={{ fontWeight: 700 }}>{m.from === 'admin' ? 'From store' : 'From customer'}</div>
                                <div style={{ marginTop: 6, color: '#475569' }}>{m.message}</div>
                                <div style={{ marginTop: 6, fontSize: '0.8rem', color: '#94a3b8' }}>{new Date(m.createdAt).toLocaleString()}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="info-group">
                        <div className="info-label">Payment Method</div>
                        <div className="info-value">
                          {selectedOrder.paymentMethod === 'cod' ? 'Cash on Delivery' : selectedOrder.paymentMethod || 'Online'}
                        </div>
                      </div>
                    </div>

                    {/* Right Column: Products & Summary */}
                    <div className="modal-section">
                      <h3 className="section-title">Products Ordered</h3>
                      
                      <div className="products-list">
                        {selectedOrder.items.map((item, i) => (
                          <motion.div 
                            key={(item.product && item.product._id) || i}
                            className="product-item"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.05 }}
                          >
                            <div className="product-image">
                              {item.product && typeof item.product === 'object' && item.product.image ? (
                                <img src={item.product.image} alt={item.product.name || 'Product'} />
                              ) : (
                                <div style={{ color: '#cbd5e0', fontSize: '2rem' }}>📦</div>
                              )}
                            </div>
                            <div className="product-info">
                              <div className="admin-modal-product-name">
                                {item.product && typeof item.product === 'object' ? item.product.name : 'Unknown Product'}
                              </div>
                              <div className="product-quantity">Quantity: {item.quantity}</div>
                            </div>
                            <div className="product-price">
                              ₱{item.product && typeof item.product === 'object' && item.product.price ? 
                                (item.product.price * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 }) : 
                                '0.00'}
                            </div>
                          </motion.div>
                        ))}
                      </div>

                      <div className="order-summary">
                        <div className="summary-row">
                          <span>Subtotal</span>
                          <span>₱{selectedOrder.items.reduce((sum, item) => sum + (item.product && typeof item.product === 'object' && item.product.price ? item.product.price * item.quantity : 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="summary-row">
                          <span>Delivery Fee</span>
                          <span>
                            {(() => {
                              if (selectedOrder.deliveryFee !== undefined) {
                                return `₱${selectedOrder.deliveryFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
                              } else {
                                const subtotal = selectedOrder.items.reduce((sum, item) => sum + (item.product && typeof item.product === 'object' && item.product.price ? item.product.price * item.quantity : 0), 0);
                                let fee = 0;
                                if (subtotal >= 10000) fee = 0;
                                else if (selectedOrder.deliveryDetails.region === 'Manila') fee = 1500;
                                else fee = 800;
                                return `₱${fee.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
                              }
                            })()}
                          </span>
                        </div>
                        {(() => {
                          const subtotal = selectedOrder.items.reduce((sum, item) => sum + (item.product && typeof item.product === 'object' && item.product.price ? item.product.price * item.quantity : 0), 0);
                          let discountValue = 0;
                          if (selectedOrder.discount && selectedOrder.discount > 0) {
                            if (selectedOrder.discountType === '%') {
                              discountValue = (subtotal * selectedOrder.discount) / 100;
                            } else if (selectedOrder.discountType === 'amount' || selectedOrder.discountType === 'fixed') {
                              discountValue = selectedOrder.discount;
                            }
                          }
                          if (discountValue > 0) {
                            return (
                              <div className="summary-row" style={{ color: '#15803d', fontWeight: 600 }}>
                                <span>
                                  Discount
                                  {selectedOrder.promoCode && (
                                    <span style={{ fontSize: '0.85rem', marginLeft: '6px', background: '#dcfce7', padding: '2px 8px', borderRadius: '4px', color: '#166534' }}>
                                      {selectedOrder.promoCode}
                                    </span>
                                  )}
                                  {selectedOrder.voucherId && !selectedOrder.promoCode && (
                                    <span style={{ fontSize: '0.85rem', marginLeft: '6px', background: '#fef3c7', padding: '2px 8px', borderRadius: '4px', color: '#92400e' }}>
                                      Reward Applied
                                    </span>
                                  )}
                                </span>
                                <span>
                                  {selectedOrder.discountType === '%'
                                    ? `-₱${discountValue.toLocaleString(undefined, { minimumFractionDigits: 2 })} (${selectedOrder.discount}%)`
                                    : `-₱${discountValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                                </span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        <div className="summary-row summary-total">
                          <span>Total Amount</span>
                          <span>₱{selectedOrder.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="action-buttons-section">
                        <h3 className="section-title">Order Actions</h3>
                        {((selectedOrder.status || 'Pending') === 'Completed') && (
                          <div style={{
                            marginBottom: 12,
                            background: '#ecfdf5',
                            color: '#065f46',
                            border: '1.5px solid #a7f3d0',
                            borderRadius: 8,
                            padding: 12,
                            fontWeight: 600
                          }}>
                            ✅ Completed orders are locked. Status can no longer be changed.
                          </div>
                        )}
                        {((selectedOrder.status || 'Pending') === 'Cancelled') && (
                          <div style={{
                            marginBottom: 12,
                            background: '#fef2f2',
                            color: '#991b1b',
                            border: '1.5px solid #fecaca',
                            borderRadius: 8,
                            padding: 12,
                            fontWeight: 600
                          }}>
                            ❌ Cancelled orders are locked. Status can no longer be changed.
                          </div>
                        )}
                        {((selectedOrder.status || 'Pending') !== 'Cancelled' && (selectedOrder.status || 'Pending') !== 'Completed') && (
                        <div className="action-buttons-grid">
                          <motion.button 
                            className="action-btn completed"
                            disabled={(selectedOrder.status || 'Pending') === 'Pending' || (selectedOrder.status || 'Pending') === 'Payment Confirmed' || !['Shipped', 'Ready for Pickup'].includes(selectedOrder.status || 'Pending') || (selectedOrder.status || 'Pending') === 'Completed' || (selectedOrder.status || 'Pending') === 'Awaiting Payment'}
                            onClick={async () => {
                              try {
                                const currentStatus = selectedOrder.status || 'Pending';
                                if (currentStatus === 'Awaiting Payment') return alert('Cannot modify order status while awaiting payment.');
                                if (currentStatus === 'Completed') return alert('Order is already completed.');
                                if (currentStatus === 'Cancelled') return alert('Cannot change status. Order is cancelled.');
                                if (!['Shipped', 'Ready for Pickup'].includes(currentStatus)) {
                                  return alert('Order must be "Shipped" or "Ready for Pickup" before marking as completed.');
                                }
                                const res = await fetch(`${process.env.REACT_APP_API}/api/orders/${selectedOrder._id}/status`, {
                                  method: 'PATCH',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                                  },
                                  body: JSON.stringify({ status: 'Completed' }),
                                });
                                if (!res.ok) {
                                  let msg = 'Failed to update status';
                                  try { const data = await res.json(); if (data && data.message) msg = data.message; } catch {}
                                  throw new Error(msg);
                                }
                                const updated = await res.json();
                                const refetchRes = await fetch(`${process.env.REACT_APP_API}/api/orders/all`, {
                                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                });
                                const allOrders = await refetchRes.json();
                                const newOrder = allOrders.find(o => o._id === updated._id);
                                setSelectedOrder(newOrder || updated);
                                setOrders(allOrders);
                              } catch (err) {
                                alert(err.message || 'Failed to mark as completed');
                              }
                            }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            ✓ Mark Completed
                          </motion.button>
                          
                          <motion.button 
                            className="action-btn preparing"
                            disabled={['Completed', 'Awaiting Payment', 'Shipped', 'Ready for Pickup', 'Preparing'].includes(selectedOrder.status || 'Pending')}
                            onClick={async () => {
                              try {
                                const currentStatus = selectedOrder.status || 'Pending';
                                if (currentStatus === 'Awaiting Payment') return alert('Cannot modify order status while awaiting payment.');
                                if (currentStatus === 'Completed') return alert('Cannot change status. Order is completed.');
                                if (currentStatus === 'Cancelled') return alert('Cannot change status. Order is cancelled.');
                                if (['Shipped', 'Ready for Pickup'].includes(currentStatus)) return alert('Cannot change status back to Preparing.');
                                const res = await fetch(`${process.env.REACT_APP_API}/api/orders/${selectedOrder._id}/status`, {
                                  method: 'PATCH',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                                  },
                                  body: JSON.stringify({ status: 'Preparing' }),
                                });
                                if (!res.ok) {
                                  let msg = 'Failed to update status';
                                  try { const data = await res.json(); if (data && data.message) msg = data.message; } catch {}
                                  throw new Error(msg);
                                }
                                const updated = await res.json();
                                const refetchRes = await fetch(`${process.env.REACT_APP_API}/api/orders/all`, {
                                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                });
                                const allOrders = await refetchRes.json();
                                const newOrder = allOrders.find(o => o._id === updated._id);
                                setSelectedOrder(newOrder || updated);
                                setOrders(allOrders);
                              } catch (err) {
                                alert(err.message || 'Failed to mark as preparing');
                              }
                            }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            🔨 Mark Preparing
                          </motion.button>
                          
                          {selectedOrder.shippingMethod !== 'pickup' && (
                          <motion.button 
                            className="action-btn shipped"
                            disabled={(selectedOrder.status || 'Pending') === 'Pending' || (selectedOrder.status || 'Pending') === 'Payment Confirmed' || ['Shipped', 'Completed', 'Awaiting Payment'].includes(selectedOrder.status || 'Pending')}
                            onClick={async () => {
                              try {
                                const currentStatus = selectedOrder.status || 'Pending';
                                if (currentStatus === 'Awaiting Payment') return alert('Cannot modify order status while awaiting payment.');
                                if (currentStatus === 'Shipped') return alert('Order is already shipped.');
                                if (currentStatus === 'Completed') return alert('Cannot change status. Order is completed.');
                                if (currentStatus === 'Cancelled') return alert('Cannot change status. Order is cancelled.');
                                const res = await fetch(`${process.env.REACT_APP_API}/api/orders/${selectedOrder._id}/status`, {
                                  method: 'PATCH',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                                  },
                                  body: JSON.stringify({ status: 'Shipped' }),
                                });
                                if (!res.ok) {
                                  let msg = 'Failed to update status';
                                  try { const data = await res.json(); if (data && data.message) msg = data.message; } catch {}
                                  throw new Error(msg);
                                }
                                const updated = await res.json();
                                const refetchRes = await fetch(`${process.env.REACT_APP_API}/api/orders/all`, {
                                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                });
                                const allOrders = await refetchRes.json();
                                const newOrder = allOrders.find(o => o._id === updated._id);
                                setSelectedOrder(newOrder || updated);
                                setOrders(allOrders);
                              } catch (err) {
                                alert(err.message || 'Failed to mark as shipped');
                              }
                            }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            🚚 Mark Shipped
                          </motion.button>
                          )}
                          
                          {selectedOrder.shippingMethod === 'pickup' && (
                            <motion.button 
                              className="action-btn ready-pickup"
                              disabled={(selectedOrder.status || 'Pending') === 'Pending' || (selectedOrder.status || 'Pending') === 'Payment Confirmed' || ['Ready for Pickup', 'Completed', 'Awaiting Payment'].includes(selectedOrder.status || 'Pending')}
                              onClick={async () => {
                                try {
                                  const currentStatus = selectedOrder.status || 'Pending';
                                  if (currentStatus === 'Awaiting Payment') return alert('Cannot modify order status while awaiting payment.');
                                  if (currentStatus === 'Ready for Pickup') return alert('Order is already ready for pickup.');
                                  if (currentStatus === 'Completed') return alert('Cannot change status. Order is completed.');
                                  if (currentStatus === 'Cancelled') return alert('Cannot change status. Order is cancelled.');
                                  const res = await fetch(`${process.env.REACT_APP_API}/api/orders/${selectedOrder._id}/status`, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${localStorage.getItem('token')}`,
                                    },
                                    body: JSON.stringify({ status: 'Ready for Pickup' }),
                                  });
                                  if (!res.ok) {
                                    let msg = 'Failed to update status';
                                    try { const data = await res.json(); if (data && data.message) msg = data.message; } catch {}
                                    throw new Error(msg);
                                  }
                                  const updated = await res.json();
                                  const refetchRes = await fetch(`${process.env.REACT_APP_API}/api/orders/all`, {
                                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                  });
                                  const allOrders = await refetchRes.json();
                                  const newOrder = allOrders.find(o => o._id === updated._id);
                                  setSelectedOrder(newOrder || updated);
                                  setOrders(allOrders);
                                } catch (err) {
                                  alert(`Failed to mark as ready for pickup: ${err.message}`);
                                }
                              }}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              📦 Ready for Pickup
                            </motion.button>
                          )}
                          
                          <motion.button 
                            className="action-btn cancelled"
                            disabled={['Awaiting Payment','Payment Confirmed','Preparing','Shipped','Ready for Pickup','Completed','Cancelled'].includes(selectedOrder.status) || (selectedOrder.paymentStatus === 'paid' || selectedOrder.status === 'Payment Confirmed')}
                            onClick={() => {
                              const status = selectedOrder.status || 'Pending';
                              if (status === 'Awaiting Payment') {
                                setErrorMessage('Cannot modify order status while awaiting payment.');
                                setShowErrorModal(true);
                                return;
                              }
                              if (status === 'Completed') {
                                setErrorMessage('Cannot change status. Order is completed.');
                                setShowErrorModal(true);
                                return;
                              }
                              if (status === 'Cancelled') {
                                setErrorMessage('Order already cancelled.');
                                setShowErrorModal(true);
                                return;
                              }
                              if (['Preparing','Shipped','Ready for Pickup'].includes(status)) {
                                setErrorMessage('Cannot cancel. Order already in fulfillment process.');
                                setShowErrorModal(true);
                                return;
                              }
                              if (selectedOrder.paymentStatus === 'paid' || status === 'Payment Confirmed') {
                                setErrorMessage('Cannot cancel an order with confirmed payment.');
                                setShowErrorModal(true);
                                return;
                              }
                              setCancelOrderData(selectedOrder);
                              setShowCancelModal(true);
                            }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            ✕ Cancel Order
                          </motion.button>
                        </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Footer */}
      <div style={{ width: '100%', background: '#175c36', minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 32 }}>
        <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 500, letterSpacing: 1 }}>
          © {new Date().getFullYear()} Chiqui's Garden
        </span>
      </div>

      <ConfirmModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setCancelOrderData(null);
        }}
        onConfirm={confirmCancelOrder}
        title="Cancel Order"
        message="Are you sure you want to cancel this order? You will be prompted to add an optional message for the customer."
        confirmText="Cancel Order"
        cancelText="Keep Order"
        type="warning"
      />

      <ConfirmModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setErrorMessage('');
        }}
        onConfirm={() => {}}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="error"
      />
    </div>
  );
}

export default AdminOrdersPage;
